import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import {
  Visibility,
  Print,
  Download,
  Email,
  Block,
  Edit,
  Delete,
  Add,
  Settings,
  Person,
  Lock,
  LockOpen,
  CheckCircle,
  Cancel,
  Refresh,
  Send,
  History,
} from '@mui/icons-material';
import Timeline from '@/components/Timeline/Timeline';

export const AUDIT_LOG_STATUS = Object.freeze({
  ACCEPTED: 'accepted',
  DENIED: 'denied',
});

export const AUDIT_ACTION_ICONS = {
  view: Visibility,
  print: Print,
  download: Download,
  email: Email,
  denied: Block,
  edit: Edit,
  delete: Delete,
  create: Add,
  add: Add,
  settings: Settings,
  user: Person,
  lock: Lock,
  unlock: LockOpen,
  approve: CheckCircle,
  reject: Cancel,
  refresh: Refresh,
  send: Send,
  history: History,
};

export const AUDIT_ACTION_COLORS = {
  view: 'info',
  print: 'secondary',
  download: 'success',
  email: 'warning',
  denied: 'error',
  edit: 'info',
  delete: 'error',
  create: 'success',
  add: 'success',
  approve: 'success',
  reject: 'error',
  lock: 'warning',
  unlock: 'success',
};

export const ACTOR_TYPE_LABELS = {
  admin: 'Admin',
  tenant: 'User',
  system: 'System',
};

export const getRelativeTimeString = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
};

export const formatTimestamp = (dateString) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
  return `${month}/${day}/${year} ${time}`;
};

export const formatRelativeTime = (dateString) => {
  const timestamp = formatTimestamp(dateString);
  const relativeTime = getRelativeTimeString(dateString);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Box component='span' sx={{ fontSize: '0.75rem' }}>
        {timestamp}
      </Box>
      <Box
        component='span'
        sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
        {relativeTime}
      </Box>
    </Box>
  );
};

const AuditTimeline = ({
  logs = [],
  loading = false,
  error = null,
  actionConfig = {},
  actorTypeLabels = ACTOR_TYPE_LABELS,
  emptyMessage,
  getActorDisplay,
  getMetadata,
  renderExtra,
  layout,
  title,
  subtitle,
}) => {
  const { t } = useTranslation();

  const getIconConfig = (item) => {
    const actionName = item.actionName;
    const customConfig = actionConfig[actionName];
    const isDenied = item.status === AUDIT_LOG_STATUS.DENIED;

    if (customConfig?.icon) {
      const IconComponent = customConfig.icon;
      return {
        component: (
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <IconComponent />
            {isDenied && (
              <Block
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  fontSize: 14,
                  color: 'error.main',
                  backgroundColor: 'background.paper',
                  borderRadius: '50%',
                }}
              />
            )}
          </Box>
        ),
        color: isDenied
          ? 'error'
          : customConfig.color || AUDIT_ACTION_COLORS[actionName] || 'grey',
      };
    }

    const DefaultIcon = AUDIT_ACTION_ICONS[actionName] || Visibility;
    return {
      component: (
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <DefaultIcon />
          {isDenied && (
            <Block
              sx={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                fontSize: 14,
                color: 'error.main',
                backgroundColor: 'background.paper',
                borderRadius: '50%',
              }}
            />
          )}
        </Box>
      ),
      color: isDenied ? 'error' : AUDIT_ACTION_COLORS[actionName] || 'grey',
    };
  };

  const getActionLabel = (item) => {
    const actionName = item.actionName;
    const customConfig = actionConfig[actionName];

    if (customConfig?.label) {
      return typeof customConfig.label === 'function'
        ? customConfig.label(t)
        : customConfig.label;
    }

    if (customConfig?.labelKey) {
      return t(customConfig.labelKey, customConfig.defaultLabel || actionName);
    }

    return actionName
      ? actionName.charAt(0).toUpperCase() + actionName.slice(1)
      : 'Unknown Action';
  };

  const defaultGetActorDisplay = (item) => {
    if (item.actorType === 'tenant') {
      const email = item.metadata?.actor?.email;
      return `By ${email || 'User'}`;
    }
    const label =
      actorTypeLabels[item.actorType] || item.actorType || 'Unknown';
    return `By ${label}`;
  };

  const defaultGetMetadata = (item) => {
    const ipAddress = item.metadata?.request?.ip;
    const comment = item.metadata?.comment;
    return {
      primary: ipAddress ? `IP: ${ipAddress}` : '',
      secondary: comment ? `Comment: ${comment}` : '',
    };
  };

  const timelineConfig = {
    getId: (item) => item.id,
    getDate: (item) => item.createdAt,
    getIcon: getIconConfig,
    getTitle: getActionLabel,
    getDescription: getActorDisplay || defaultGetActorDisplay,
    getMetadata: getMetadata || defaultGetMetadata,
    renderActions: () => null,
    renderExtra: renderExtra || (() => null),
    dateFormat: formatRelativeTime,
    layout: {
      dateWidth: 150,
      lineWidth: 2,
      lineSpacing: 40,
      itemSpacing: 2,
      ...layout,
    },
    empty: {
      message: emptyMessage || t('auditTimeline.empty', 'No activity yet'),
      icon: null,
    },
    loading: {
      skeletonCount: 3,
      skeletonHeight: 60,
    },
  };

  const sortedLogs = [...logs].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  return (
    <Timeline
      items={sortedLogs}
      config={timelineConfig}
      loading={loading}
      error={error}
      title={title}
      subtitle={subtitle}
    />
  );
};

export default AuditTimeline;
